import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useOutlet } from 'react-router-dom';
import ProfileLeftBar from './components/leftbar/ProfileLeftBar';
import ProfileBackground from '../shared-components/ProfileBackground';
import ProfileTabs from './components/ProfileTabs';
import { createPortal } from 'react-dom';
import {
  useCheckAccessAndBlockedQuery,
  useCheckHasCommonFriendQuery,
} from '../../../store/apiSlice/friendApi';
import { useCurrentPageUserId } from '../../../hooks/useCurrentPageUserId';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';
import { selectAuthData } from '../../../store/selectors/authSelector';
import { UserRoleEnum } from '../../../enums/user-data-field-enum';
import {
  useGetMeQuery,
  useGetUserQuery,
} from '../../../store/apiSlice/userApi';
import { PROFILE_PAGE_INCLUDES } from './constants/profile-includes';

const PersonalInfoPage = () => {
  const { userId, isOwnPage } = useCurrentPageUserId();
  const outlet = useOutlet();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('lg'));

  const navigate = useNavigate();

  const authData = useSelector(selectAuthData);
  const viewerRole = authData?.user?.role;
  const isCompanyViewer = viewerRole === UserRoleEnum.COMPANY;

  // Unified user query: own profile uses /me, others use /:userId
  // Use PROFILE_PAGE_INCLUDES so all needed relations are loaded once.
  const { data: meUser } = useGetMeQuery(PROFILE_PAGE_INCLUDES, {
    skip: !isOwnPage,
  });

  const { data: pageUser } = useGetUserQuery(
    { userId: userId || '', includes: PROFILE_PAGE_INCLUDES },
    {
      skip: !userId || isOwnPage,
    }
  );

  const user = isOwnPage ? meUser : pageUser;
  const profileBanner = document.getElementById('profile-banner');

  const {
    data: isHaveAccessAndBlockedData,
    isLoading: isHaveAccessAndBlockedDataLoading,
  } = useCheckAccessAndBlockedQuery(userId ?? skipToken);

  const { data: checkHasCommonFriendData } = useCheckHasCommonFriendQuery(
    userId ?? skipToken,
    { skip: isOwnPage || isCompanyViewer }
  );

  return (
    <>
      {profileBanner &&
        createPortal(
          <ProfileBackground banner={user?.media?.banner ?? null} />,
          profileBanner
        )}

      <Box
        display="flex"
        flexDirection="column"
        height="fit-content"
        sx={{
          width: '100%',
          marginTop: {
            xs: '-20px',
            md: '-24px',
            lg: '-28px',
            xl: '-36px',
          },
          position: 'static',
        }}
      >
        <Box
          display="flex"
          color="#DDE1E8"
          maxWidth="1440px"
          width="100%"
          alignSelf="center"
          flexDirection={mobileView ? 'column' : 'row'}
        >
          <ProfileLeftBar
            styles={undefined}
            navigatePhotos={() => navigate(`?tab=${4}`)}
            navigateVideos={() => navigate(`?tab=${5}`)}
            hasAccess={isHaveAccessAndBlockedData?.data?.hasAccess}
            isBlocked={isHaveAccessAndBlockedData?.data?.isBlocked}
            hasMutualFriends={checkHasCommonFriendData?.data}
            user={user}
          />
          <Box
            className="profile-info-page-main-content"
            flex={2}
            display="flex"
            flexDirection="column"
            minWidth={0}
          >
            {outlet || (
              <ProfileTabs
                hasAccess={isHaveAccessAndBlockedData?.data?.hasAccess}
                isBlocked={isHaveAccessAndBlockedData?.data?.isBlocked}
                isAccessLoading={isHaveAccessAndBlockedDataLoading}
                user={user}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default PersonalInfoPage;
